import { useEffect, useRef, useState } from 'react';

import { useAppDispatch } from '@/shared/hooks';

import { authModel } from '@/entities/auth';

import { MFA_CODE_LENGTH, MFACredentialAction } from '../config';

import { useMFAErrorMessage } from './useMFAErrorMessage';

type UseMFAEnrollmentResult = {
  secret: string;
  otpauthURI: string;
  code: string;
  errorMessage: string;
  recoveryCodes: string[];
  isStarting: boolean;
  isConfirming: boolean;
  isCodeComplete: boolean;
  isCurrentCodeRequired: boolean;
  signInRequired: boolean;
  setCode: (code: string) => void;
  confirm: (code: string) => Promise<void>;
};

/** Drives MFA_Enroll_Begin/Confirm; a replacement begins after the current code. */
export const useMFAEnrollment = (
  action: MFACredentialAction = 'enroll',
): UseMFAEnrollmentResult => {
  const dispatch = useAppDispatch();

  const [isCurrentCodeRequired, setIsCurrentCodeRequired] = useState(
    action === 'replace',
  );
  const getErrorMessage = useMFAErrorMessage(
    isCurrentCodeRequired ? 'verify' : 'setup',
  );

  const [secret, setSecret] = useState('');
  const [otpauthURI, setOtpauthURI] = useState('');
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [signInRequired, setSignInRequired] = useState(false);
  const [isStarting, setIsStarting] = useState(action === 'enroll');
  const [isConfirming, setIsConfirming] = useState(false);

  // Each Begin replaces the pending secret server-side, so StrictMode's
  // double-invoked effect must not issue a second one.
  const hasBegunRef = useRef(false);

  // A ref, not the state flag: a click can land in the same tick as the
  // auto-submit on the sixth digit.
  const isConfirmingRef = useRef(false);

  // Keeps the MFA gate from unmounting this screen while it shows the codes.
  const confirmRequestIDRef = useRef<string>();

  useEffect(
    () => () => {
      if (confirmRequestIDRef.current) {
        dispatch(
          authModel.actions.finishMFARecoveryCodes(confirmRequestIDRef.current),
        );
      }
    },
    [],
  );

  const begin = async (currentCode?: string) => {
    const response = await dispatch(
      authModel.thunks.mfaEnrollBegin({ CurrentCode: currentCode }),
    ).unwrap();

    setSecret(response.Secret);
    setOtpauthURI(response.OtpauthURI);
    setCode('');
    setIsCurrentCodeRequired(false);
  };

  useEffect(() => {
    if (action !== 'enroll' || hasBegunRef.current) {
      return;
    }

    hasBegunRef.current = true;

    const start = async () => {
      try {
        await begin();
      } catch (error) {
        setErrorMessage(getErrorMessage(error));
      } finally {
        setIsStarting(false);
      }
    };

    start();
  }, []);

  const changeCode = (nextCode: string) => {
    setCode(nextCode);
    setErrorMessage('');
  };

  const confirm = async (confirmedCode: string) => {
    if (confirmedCode.length !== MFA_CODE_LENGTH || isConfirmingRef.current) {
      return;
    }

    isConfirmingRef.current = true;
    setIsConfirming(true);

    try {
      if (isCurrentCodeRequired) {
        await begin(confirmedCode);
        return;
      }

      const request = dispatch(
        authModel.thunks.mfaEnrollConfirm({ Code: confirmedCode }),
      );
      confirmRequestIDRef.current = request.requestId;
      const response = await request.unwrap();

      setRecoveryCodes(response.RecoveryCodes);
      setSignInRequired(response.SignInRequired);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
      setCode('');
    } finally {
      isConfirmingRef.current = false;
      setIsConfirming(false);
    }
  };

  return {
    secret,
    otpauthURI,
    code,
    errorMessage,
    recoveryCodes,
    isStarting,
    isConfirming,
    isCodeComplete: code.length === MFA_CODE_LENGTH,
    isCurrentCodeRequired,
    signInRequired,
    setCode: changeCode,
    confirm,
  };
};
